
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Copy, Eye, Loader2, Bug } from 'lucide-react';
import { toast } from 'sonner';

interface SitemapPreviewProps {
  onGenerateXML: () => Promise<string>;
  isGenerating: boolean;
}

export const SitemapPreview: React.FC<SitemapPreviewProps> = ({
  onGenerateXML,
  isGenerating,
}) => {
  const [xmlContent, setXmlContent] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  const handleGenerateAndPreview = async () => {
    try {
      if (debugMode) {
        console.log('🐛 Debug mode enabled - check console for detailed logs');
      }
      
      const xml = await onGenerateXML();
      setXmlContent(xml);
      setShowPreview(true);
      
      if (debugMode) {
        console.log('✅ XML generation completed successfully');
        console.log('📊 Generated XML length:', xml.length);
      }
    } catch (error) {
      console.error('💥 Error in preview generation:', error);
      
      if (debugMode) {
        console.log('🐛 Debug info - Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    }
  };

  const handleDownload = () => {
    if (!xmlContent) return;
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Sitemap downloaded successfully');
  };

  const handleCopyToClipboard = async () => {
    if (!xmlContent) return;
    
    try {
      await navigator.clipboard.writeText(xmlContent);
      toast.success('Sitemap XML copied to clipboard');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Sitemap Generator</CardTitle>
          <CardDescription>
            Generate and preview your sitemap.xml file with enhanced logging
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleGenerateAndPreview}
              disabled={isGenerating}
              className="flex items-center gap-2"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              Generate & Preview
            </Button>
            
            <Button
              onClick={() => setDebugMode(!debugMode)}
              variant={debugMode ? "default" : "outline"}
              className="flex items-center gap-2"
            >
              <Bug className="h-4 w-4" />
              Debug Mode
            </Button>
            
            {xmlContent && (
              <>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
                
                <Button
                  onClick={handleCopyToClipboard}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </>
            )}
          </div>

          {debugMode && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                🐛 Debug mode is enabled. Check the browser console for detailed generation logs.
              </p>
            </div>
          )}

          {isGenerating && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                ⏳ Generating sitemap... Check the Generation Logs tab for real-time status updates.
              </p>
            </div>
          )}

          {showPreview && xmlContent && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">XML Preview:</h4>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-4 max-h-96 overflow-auto">
                <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {xmlContent}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
